import React, { useState } from "react";
import classNames from "classnames";
import ModalShell from "./ModalShell";
import { ModalData } from "./modalBuilders";
import styles from "./DrillDownModal.module.scss";

interface DrillDownModalProps {
  data: ModalData;
  onClose: () => void;
  onGenerateReport: () => void;
  onGenerateCertificate: () => void;
}

/** Campus / student / content drill-down with stat tiles + tabbed lists. */
export default function DrillDownModal({
  data,
  onClose,
  onGenerateReport,
  onGenerateCertificate,
}: DrillDownModalProps) {
  const [tab, setTab] = useState(0);
  const activeTab = data.tabs[Math.min(tab, data.tabs.length - 1)];
  const showTabs = data.tabs.length > 1;

  // The school panel links a drill-down through to that student's / teacher's
  // own page and can print the top performer a certificate. Neither exists in
  // the admin panel, so both footer actions are left out here.
  const profileBtn = null;
  const certificateBtn = null;

  // campus & student drill-downs have no report footer — they get the profile
  // link and the certificate instead (whichever the record supports)
  const reportFooter = data.kind !== "student" && data.kind !== "campus";
  const footer = !reportFooter ? (
    (profileBtn || certificateBtn) && (
      // no explanatory note here — the actions speak for themselves
      <div className={classNames(styles.footActions, styles.footActionsOnly)}>
        {certificateBtn}
        {profileBtn}
      </div>
    )
  ) : (
    <>
      <span className={styles.footNote}>
        Full breakdown available in the Monthly Report
      </span>
      <div className={styles.footActions}>
        {profileBtn}
        {data.cert && (
          <button type="button" className={styles.certBtn} onClick={onGenerateCertificate}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="9" r="6" />
              <path d="M9 14.5 8 22l4-2 4 2-1-7.5" />
            </svg>
            Certificate
          </button>
        )}
        <button type="button" className={styles.reportBtn} onClick={onGenerateReport}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 3v4a1 1 0 0 0 1 1h4" />
            <path d="M5 3h9l5 5v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
          </svg>
          Generate Report
        </button>
      </div>
    </>
  );

  return (
    <ModalShell
      title={data.title}
      subtitle={data.subtitle}
      onClose={onClose}
      footer={footer || undefined}
      maxWidth={660}
      bodyClassName={styles.body}
    >
      <div
        className={classNames(
          styles.statsRow,
          data.stats.length === 5 && styles.statsRow5,
        )}
      >
        {data.stats.map((m, i) => (
          <div className={styles.statBig} key={i}>
            <div className={styles.statBigValue}>{m.value}</div>
            <div className={styles.statBigLabel}>{m.label}</div>
          </div>
        ))}
      </div>

      {data.stats2.length > 0 && (
        <div className={styles.stats2Row}>
          {data.stats2.map((m, i) => (
            <div className={styles.stat2} key={i}>
              <div className={styles.stat2Value} style={{ color: m.color }}>
                {m.value}
              </div>
              <div className={styles.stat2Label}>{m.label}</div>
            </div>
          ))}
        </div>
      )}

      {showTabs && (
        <div className={styles.tabs}>
          {data.tabs.map((t, i) => (
            <button
              type="button"
              key={t.label}
              className={classNames(styles.tab, i === tab && styles.tabActive)}
              onClick={() => setTab(i)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div className={styles.listTitle}>{activeTab.listTitle}</div>
      {activeTab.rows.map((row) => (
        <div className={styles.listRow} key={row.rank}>
          <span className={styles.listRank}>{row.rank}</span>
          <div className={styles.listNameWrap}>
            <div className={styles.listName}>{row.name}</div>
            {row.hasSub && <div className={styles.listSub}>{row.sub}</div>}
          </div>
          <div className={styles.listTrack}>
            <div className={styles.listFill} style={{ width: row.bar }} />
          </div>
          <div className={styles.listValue}>{row.value}</div>
        </div>
      ))}
    </ModalShell>
  );
}
